import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  Check, X, Zap, ArrowLeft, Lock, ChevronRight
} from 'lucide-react';
import CryptoCheckout from '../components/forms/CryptoCheckout';
import { PAYMENT_TYPES } from '../services/api';
import toast from 'react-hot-toast';
import { useLocale } from '../contexts/LocaleContext';

function FeatureRow({ label, starter, business, enterprise }) {
  const fmt = (v) => {
    if (v === true) return <Check className="w-4 h-4 mx-auto" style={{ color: 'var(--accent-success)' }} />;
    if (v === false) return <X className="w-4 h-4 mx-auto" style={{ color: 'var(--text-muted)' }} />;
    if (typeof v === 'number' && v >= 99999) return <span style={{ color: 'var(--accent-success)' }}>∞</span>;
    return <span className="font-mono text-xs">{v}</span>;
  };
  return (
    <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
      <td className="px-4 py-2.5 text-sm text-left" style={{ color: 'var(--text-secondary)' }}>{label}</td>
      <td className="px-4 py-2.5 text-center text-sm">{fmt(starter)}</td>
      <td className="px-4 py-2.5 text-center text-sm bg-[var(--accent-primary)]/5">{fmt(business)}</td>
      <td className="px-4 py-2.5 text-center text-sm">{fmt(enterprise)}</td>
    </tr>
  );
}

function PlanCard({ plan, isCurrentPlan, billingCycle, onSelect, selected, t }) {
  const price = billingCycle === 'yearly' ? plan.yearly_price / 12 : plan.monthly_price;
  const isPopular = plan.name === 'Business';
  const isSelected = selected?.id === plan.id;

  const features = [
    plan.max_traders >= 99999
      ? t('upgradePage.unlimitedTraders')
      : t('upgradePage.tradersCount').replace('{count}', plan.max_traders.toLocaleString()),
    plan.max_admins >= 99999
      ? t('upgradePage.unlimitedAdmins')
      : t('upgradePage.adminsCount').replace('{count}', plan.max_admins),
    plan.max_instruments >= 99999
      ? t('upgradePage.unlimitedInstruments')
      : t('upgradePage.instrumentsCount').replace('{count}', plan.max_instruments),
    plan.feed_latency === 'ultra' ? t('upgradePage.feedUltra')
      : plan.feed_latency === 'priority' ? t('upgradePage.feedPriority')
      : t('upgradePage.feedStandard'),
    plan.api_access && t('upgradePage.fullApiAccess'),
    plan.allow_forex && t('instruments.forex'),
    plan.allow_crypto && t('instruments.crypto'),
    plan.allow_commodities && t('instruments.commodities'),
    plan.allow_indices && t('instruments.indices'),
    plan.allow_stocks && t('instruments.stocks'),
  ].filter(Boolean);

  return (
    <div
      onClick={() => !isCurrentPlan && onSelect(plan)}
      className={`relative rounded-2xl border-2 p-6 transition-all cursor-pointer ${
        isCurrentPlan ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.01]'
      }`}
      style={{
        borderColor: isSelected ? 'var(--accent-primary)' : isPopular ? 'var(--accent-primary)40' : 'var(--border-color)',
        backgroundColor: isSelected ? 'var(--accent-primary)08' : 'var(--bg-secondary)',
      }}
    >
      {isPopular && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {t('upgradePage.mostPopular')}
          </span>
        </div>
      )}
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: 'var(--accent-success)' }}>
            {t('upgradePage.currentPlan')}
          </span>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{plan.name}</h3>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-3xl font-black font-mono" style={{ color: 'var(--text-primary)' }}>
            ${Math.round(price).toLocaleString()}
          </span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('upgradePage.perMonth')}</span>
        </div>
        {billingCycle === 'yearly' && (
          <p className="text-xs mt-1" style={{ color: 'var(--accent-success)' }}>
            {t('upgradePage.saveYearly').replace('{amount}', Math.round(plan.yearly_price).toLocaleString())}
          </p>
        )}
      </div>

      <ul className="space-y-2 mb-5">
        {features.map((feat, i) => (
          <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--accent-success)' }} />
            {feat}
          </li>
        ))}
      </ul>

      {!isCurrentPlan && (
        <div
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all"
          style={{
            backgroundColor: isSelected ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
            color: isSelected ? 'white' : 'var(--text-primary)',
          }}
        >
          {isSelected ? t('upgradePage.selected') : t('upgradePage.selectPlan')}
          {!isSelected && <ChevronRight className="w-4 h-4" />}
        </div>
      )}
    </div>
  );
}

export default function UpgradePage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { subscription, accessStatus, graceDaysRemaining, refetch } = useSubscription();

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const isRenewal = accessStatus === 'grace' || accessStatus === 'expired';

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch('/api/broker/subscription/plans', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      });
      const data = await res.json();
      if (data.success) setPlans(data.data);
    } catch {
      toast.error(t('upgradePage.failedToLoadPlans'));
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleProceedToCheckout = () => {
    if (!selectedPlan) { toast.error(t('upgradePage.selectPlanFirst')); return; }
    setShowCheckout(true);
  };

  const handlePaymentSuccess = async () => {
    await refetch();
    toast.success(t('upgradePage.planActivated').replace('{name}', selectedPlan.name));
    navigate('/');
  };

  const currentPlanId = subscription?.plan_id;
  const days = graceDaysRemaining ?? 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b px-6 py-4 flex items-center gap-4"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
            {isRenewal ? t('upgradePage.renewTitle') : t('upgradePage.upgradeTitle')}
          </h1>
          {subscription && (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {t('upgradePage.currentlyOn')} <strong>{subscription.plan_name}</strong>
              {accessStatus === 'grace' && graceDaysRemaining !== null && (
                <span style={{ color: 'var(--accent-danger)' }}>
                  {' — '}
                  {days === 1
                    ? t('upgradePage.graceDaysLeft').replace('{days}', days)
                    : t('upgradePage.graceDaysLeftPlural').replace('{days}', days)}
                </span>
              )}
              {accessStatus === 'expired' && (
                <span style={{ color: 'var(--accent-danger)' }}> — {t('upgradePage.expired')}</span>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* Expiry alert */}
        {isRenewal && (
          <div className="rounded-2xl p-5 flex items-start gap-4"
            style={{ backgroundColor: 'var(--accent-danger)10', border: '1px solid var(--accent-danger)30' }}>
            <Lock className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-danger)' }} />
            <div>
              <p className="font-bold text-sm" style={{ color: 'var(--accent-danger)' }}>
                {accessStatus === 'grace'
                  ? (days === 1
                      ? t('upgradePage.graceWarning').replace('{days}', days)
                      : t('upgradePage.graceWarningPlural').replace('{days}', days))
                  : t('upgradePage.expiredWarning')}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {t('upgradePage.renewOrUpgrade')}
              </p>
            </div>
          </div>
        )}

        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-1 p-1 rounded-xl border"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            {['monthly', 'yearly'].map((cycle) => (
              <button key={cycle} onClick={() => setBillingCycle(cycle)}
                className="px-5 py-2 rounded-lg text-sm font-semibold transition-all capitalize"
                style={{
                  backgroundColor: billingCycle === cycle ? 'var(--accent-primary)' : 'transparent',
                  color: billingCycle === cycle ? 'white' : 'var(--text-secondary)',
                }}>
                {cycle === 'monthly' ? t('upgradePage.monthly') : t('upgradePage.yearly')}
                {cycle === 'yearly' && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: billingCycle === 'yearly' ? 'white' : 'var(--accent-success)20',
                      color: billingCycle === 'yearly' ? 'var(--accent-primary)' : 'var(--accent-success)',
                    }}>
                    -10%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        {loadingPlans ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={plan.id === currentPlanId && accessStatus === 'active'}
                billingCycle={billingCycle}
                selected={selectedPlan}
                onSelect={setSelectedPlan}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Feature comparison table */}
        {!loadingPlans && plans.length >= 3 && (
          <div className="rounded-2xl border overflow-hidden"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <p className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                {t('upgradePage.fullComparison')}
              </p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>{t('upgradePage.featureCol')}</th>
                  {plans.map(p => (
                    <th key={p.id} className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wide"
                      style={{ color: p.name === 'Business' ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <FeatureRow label={t('upgradePage.featureTraders')}     starter={plans[0]?.max_traders}     business={plans[1]?.max_traders}     enterprise={plans[2]?.max_traders} />
                <FeatureRow label={t('upgradePage.featureAdmins')}      starter={plans[0]?.max_admins}      business={plans[1]?.max_admins}      enterprise={plans[2]?.max_admins} />
                <FeatureRow label={t('upgradePage.featureInstruments')} starter={plans[0]?.max_instruments} business={plans[1]?.max_instruments} enterprise={plans[2]?.max_instruments} />
                <FeatureRow label={t('upgradePage.featureFeedLatency')} starter={plans[0]?.feed_latency}    business={plans[1]?.feed_latency}    enterprise={plans[2]?.feed_latency} />
                <FeatureRow label={t('upgradePage.featureApiAccess')}   starter={plans[0]?.api_access}      business={plans[1]?.api_access}      enterprise={plans[2]?.api_access} />
                <FeatureRow label={t('upgradePage.featureForex')}       starter={plans[0]?.allow_forex}     business={plans[1]?.allow_forex}     enterprise={plans[2]?.allow_forex} />
                <FeatureRow label={t('upgradePage.featureCrypto')}      starter={plans[0]?.allow_crypto}    business={plans[1]?.allow_crypto}    enterprise={plans[2]?.allow_crypto} />
                <FeatureRow label={t('upgradePage.featureCommodities')} starter={plans[0]?.allow_commodities} business={plans[1]?.allow_commodities} enterprise={plans[2]?.allow_commodities} />
                <FeatureRow label={t('upgradePage.featureIndices')}     starter={plans[0]?.allow_indices}   business={plans[1]?.allow_indices}   enterprise={plans[2]?.allow_indices} />
                <FeatureRow label={t('upgradePage.featureStocks')}      starter={plans[0]?.allow_stocks}    business={plans[1]?.allow_stocks}    enterprise={plans[2]?.allow_stocks} />
              </tbody>
            </table>
          </div>
        )}

        {/* CTA footer */}
        <div className="sticky bottom-0 py-4 border-t -mx-4 px-4 flex items-center justify-between gap-4"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <div>
            {selectedPlan ? (
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {t('upgradePage.selectedPlan')}{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{selectedPlan.name}</strong>
                {' — '}
                <span className="font-mono font-bold" style={{ color: 'var(--accent-primary)' }}>
                  ${billingCycle === 'yearly'
                    ? Math.round(selectedPlan.yearly_price).toLocaleString()
                    : Math.round(selectedPlan.monthly_price).toLocaleString()}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {billingCycle === 'yearly' ? t('upgradePage.perYear') : t('upgradePage.perMonth')}
                </span>
              </p>
            ) : (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {t('upgradePage.selectToContinue')}
              </p>
            )}
          </div>
          <button
            onClick={handleProceedToCheckout}
            disabled={!selectedPlan}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Zap className="w-4 h-4" />
            {isRenewal ? t('upgradePage.renewNow') : t('upgradePage.upgradeNow')}
          </button>
        </div>
      </div>

      {showCheckout && selectedPlan && (
        <CryptoCheckout
          type={PAYMENT_TYPES.SUBSCRIPTION}
          item={{
            plan_id: selectedPlan.id,
            name: selectedPlan.name,
            price: billingCycle === 'yearly' ? selectedPlan.yearly_price : selectedPlan.monthly_price,
            billing_cycle: billingCycle,
          }}
          onClose={() => setShowCheckout(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}